/**
 * AdjustmentFilter - Simple filter for light adjustments
 * Feature: 011-light-editing
 * Task: T022
 *
 * Provides exposure and contrast adjustments.
 * Uses a simplified approach that's compatible with PixiJS 8.
 */

import { ColorMatrixFilter } from 'pixi.js'
import { exposureToMultiplier, contrastToMultiplier } from '../utils/adjustmentConversions'

export interface AdjustmentFilterOptions {
  /** Exposure adjustment in EV stops (-2.0 to +2.0) */
  exposure?: number

  /** Contrast adjustment (-1.0 to +1.0) */
  contrast?: number
}

/**
 * Simple filter for light adjustments (exposure and contrast)
 *
 * Uses PixiJS ColorMatrixFilter with manual matrix manipulation.
 * This is a simpler, more compatible approach than custom shaders.
 *
 * @example
 * const filter = new AdjustmentFilter({ exposure: 0.5, contrast: 0.2 })
 * sprite.filters = [filter]
 *
 * // Update adjustments
 * filter.setExposure(1.0)
 * filter.setContrast(0.5)
 */
export class AdjustmentFilter extends ColorMatrixFilter {
  private _exposure: number = 0
  private _contrast: number = 0

  constructor(options: AdjustmentFilterOptions = {}) {
    super()

    // Set initial values
    const exposure = options.exposure ?? 0
    const contrast = options.contrast ?? 0

    this._exposure = exposure
    this._contrast = contrast
    this._updateMatrix()
  }

  /**
   * Sets the exposure adjustment in EV stops
   * @param ev - Exposure value (-2.0 to +2.0)
   */
  setExposure(ev: number): void {
    this._exposure = ev
    this._updateMatrix()
  }

  /**
   * Gets the current exposure adjustment in EV stops
   * @returns Current exposure value
   */
  getExposure(): number {
    return this._exposure
  }

  /**
   * Sets the contrast adjustment
   * @param contrast - Contrast value (-1.0 to +1.0)
   */
  setContrast(contrast: number): void {
    this._contrast = contrast
    this._updateMatrix()
  }

  /**
   * Gets the current contrast adjustment
   * @returns Current contrast value
   */
  getContrast(): number {
    return this._contrast
  }

  /**
   * Resets all adjustments to neutral values
   */
  reset(): void {
    this._exposure = 0
    this._contrast = 0
    this._updateMatrix()
  }

  /**
   * Updates the color matrix with current exposure and contrast values
   * @private
   */
  private _updateMatrix(): void {
    // Get brightness and contrast multipliers
    const brightness = exposureToMultiplier(this._exposure)
    const contrast = contrastToMultiplier(this._contrast)

    // Manually construct the color matrix
    // Format: [R, G, B, A, offset] for each channel
    // ColorMatrix requires exactly 20 elements (4 rows × 5 columns)
    this.matrix = [
      // Red channel
      brightness * contrast, 0, 0, 0, (1 - contrast) * 0.5,
      // Green channel
      0, brightness * contrast, 0, 0, (1 - contrast) * 0.5,
      // Blue channel
      0, 0, brightness * contrast, 0, (1 - contrast) * 0.5,
      // Alpha channel (unchanged)
      0, 0, 0, 1, 0,
    ] as any
  }
}
